import { createContext, useContext, useCallback, useRef, useEffect, useState } from "react";
import useChatList from "../hooks/useChatList";
import useChatStreaming from "../hooks/useChatStreaming";
import useChatbotRelay from "../hooks/useChatbotRelay";
import { parseWebSocketMessage, buildSendPayload, createChat } from "./chatMessageUtils";

const ChatContext = createContext();
const CHATBOT_ENTITIES_WAKE_TOKEN = "__GET_ENTITIES__";

const ChatProvider = ({ children, config = {} }) => {
    const { tagRegistry = {}, wsUrl } = config;
    const [isLinkingWhatsApp, setIsLinkingWhatsApp] = useState(false);

    const {
        chats,
        activeChatIds,
        visibleChats,
        hiddenChats,
        addChat,
        removeChat,
        appendMessage,
        popLastMessage,
        setBotTyping,
        mergeEntities,
        activateChat,
        deactivateChat,
        setConversationId,
    } = useChatList();

    const sendRef = useRef(null);
    const chatOptionsRef = useRef({});

    const onStreamComplete = useCallback((chatId, message) => {
        appendMessage(chatId, message);
    }, [appendMessage]);

    const { streamingState, startStream, cancelStream } = useChatStreaming({
        onStreamComplete,
    });

    const { openConnection, closeConnection, send } = useChatbotRelay({
        wsUrl,
        onBotTyping: setBotTyping,
        onRawMessage: (chatId, rawData) => {
            console.log('[ChatProvider rawData] 🚀 ', JSON.stringify(rawData, null, 2));
            const { ignoreSpecial = false } = chatOptionsRef.current[chatId] ?? {};
            const parsed = parseWebSocketMessage(rawData, { ignoreSpecial });
            console.log('[ChatProvider] 🚀 parsed:', parsed);
            if (parsed.conversationId) {
                setConversationId(chatId, parsed.conversationId);
                localStorage.setItem(`chat-${chatId}`, JSON.stringify({
                    conversationId: parsed.conversationId,
                    createdAt: new Date().toISOString(),
                }));
            }
            if (parsed.type === 'history') appendMessage(chatId, parsed.message);
            if (parsed.type === 'history_api') { popLastMessage(chatId); appendMessage(chatId, parsed.message); }
            if (parsed.type === 'stream') startStream({ chatId, text: parsed.text });
            if (parsed.type === 'api') {
                cancelStream();
                appendMessage(chatId, parsed.message);
            }
            if (parsed.type === 'entities') {
                mergeEntities(chatId, parsed.entities);
                const entitiesText = Object.entries(parsed.entities || {})
                    .map(([key, value]) => `${key}: ${value}`)
                    .join('\n');
                appendMessage(chatId, {
                    type: 'info',
                    text: entitiesText || "No entities collected yet.",
                    timestamp: new Date().toISOString(),
                });
            }
        },
    });

    useEffect(() => {
        sendRef.current = send;
    }, [send]);

    useEffect(() => {
        const keys = Object.keys(localStorage).filter(k => k.startsWith('chat-'));
        keys.forEach(key => {
            try {
                const data = JSON.parse(localStorage.getItem(key));
                const chatId = key.replace('chat-', '');
                const { conversationId, scenarioId } = data;
                if (!conversationId) return;
                const chat = createChat({ name: 'Restored Chat', id: scenarioId }, conversationId);
                chat.id = chatId;
                addChat(chat);
            } catch (e) {
                console.warn(`Failed to restore chat from ${key}:`, e);
            }
        });
    }, []);

    const startNewChat = (scenario, extraParams = {}, { ignoreSpecial = false } = {}) => {
        const chat = createChat(scenario);
        chatOptionsRef.current[chat.id] = { ignoreSpecial };
        addChat(chat);
        activateChat(chat.id);
        openConnection(chat.id, scenario?.id, null, extraParams);
        return chat.id;
    };

    const openChat = (chatId) => {
        const chat = chats.find(c => c.id === chatId);
        activateChat(chatId);
        const conversationId = chat?.conversationId;
        const scenarioId = null;
        openConnection(chatId, scenarioId, conversationId);
    };

    const closeChat = (chatId) => {
        closeConnection(chatId);
        removeChat(chatId);
        delete chatOptionsRef.current[chatId];
    };

    const sendMessage = useCallback((message, chatId) => {
        const userMessage = {
            ...message,
            user: "user",
            timestamp: new Date().toISOString(),
        };

        if (message.type === 'text' && message.text.trim().toLowerCase() === 'show data') {
            appendMessage(chatId, userMessage);
            setBotTyping(chatId, true);
            if (!send(chatId, { type: 'text', text: CHATBOT_ENTITIES_WAKE_TOKEN })) {
                console.error(`Relay for chat ${chatId} not ready.`);
                setBotTyping(chatId, false);
            }
            return;
        }

        appendMessage(chatId, userMessage);
        setBotTyping(chatId, true);

        const payload = buildSendPayload(message);
        if (payload && !send(chatId, payload)) {
            console.error(`Relay for chat ${chatId} not ready.`);
            setBotTyping(chatId, false);
        }
    }, [appendMessage, setBotTyping, send]);

    const linkWhatsApp = async (phoneNumber, chat) => {
        if (!phoneNumber.trim() || !chat?.conversationId) return;
        setIsLinkingWhatsApp(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/whatsapp/link', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ conversationId: chat?.conversationId, phoneNumber }),
            });

            console.log('[ChatProvider] 🚀 ', JSON.stringify({ phoneNumber, id: chat?.id }, null, 2));

            if (res.ok) {
                await fetch('/api/whatsapp/start', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ phoneNumber }),
                });
            }
        } catch (err) {
            console.error('Failed to link WhatsApp hook  :', err.message);
        } finally {
            setIsLinkingWhatsApp(false);
        }
    };

    return (
        <ChatContext.Provider value={{ startNewChat, openChat, closeChat, hiddenChats, visibleChats, streamingState, sendMessage, tagRegistry, linkWhatsApp }}>
            {children}
        </ChatContext.Provider>
    );
};

export default ChatProvider;

export const useChat = () => useContext(ChatContext);
